import { Controller } from "@hotwired/stimulus"

const OPSLAG = "boekt-api-sleutel"
const GEWIJZIGD = "boekt:api-sleutel-gewijzigd"

// De probeerknop in de API-documentatie, zoals Brevo hem heeft. Drie dingen
// waar dit op let:
//
// 1. De sleutel blijft in deze browser. Hij gaat naar de API en verder nergens
//    heen, dus hij komt nooit in ons logboek terecht.
// 2. Het voorvoegsel kiest de omgeving. gt_test_ is de proefomgeving; alles
//    daarbuiten is de echte, en daar maakt een POST bij een echte klant ook
//    echt iets aan. Schrijven vraagt daarom eerst een vinkje.
// 3. Elke schrijfaanroep krijgt een idempotentiesleutel mee, zodat twee keer
//    drukken geen twee facturen geeft. Dat is precies waar die kop voor is.
export default class extends Controller {
  static targets = ["key", "environment", "path", "body", "output", "status", "confirm", "confirmRow"]
  static values = {
    liveBase: String, sandboxBase: String, verb: String, template: String,
    sandboxLabel: String, liveLabel: String, needsKey: String, needsConfirm: String
  }

  connect() {
    if (this.hasKeyTarget) this.keyTarget.value = this.key()
    this.luisteraar = () => this.showEnvironment()
    window.addEventListener(GEWIJZIGD, this.luisteraar)
    this.showEnvironment()
  }

  disconnect() {
    window.removeEventListener(GEWIJZIGD, this.luisteraar)
  }

  remember() {
    try {
      sessionStorage.setItem(OPSLAG, this.keyTarget.value.trim())
    } catch (_error) {
      // Privémodus weigert opslag; de sleutel werkt dan alleen deze pagina lang.
    }
    window.dispatchEvent(new CustomEvent(GEWIJZIGD))
  }

  key() {
    try {
      return sessionStorage.getItem(OPSLAG) || ""
    } catch (_error) {
      return ""
    }
  }

  sandbox() {
    const key = this.key()
    return key === "" || key.startsWith("gt_test_")
  }

  // Wat de gebruiker ziet voordat hij drukt: welke omgeving hij raakt.
  showEnvironment() {
    const sandbox = this.sandbox()
    if (this.hasEnvironmentTarget) {
      this.environmentTarget.textContent = sandbox ? this.sandboxLabelValue : this.liveLabelValue
      this.environmentTarget.classList.toggle("text-signal", !sandbox)
      this.environmentTarget.classList.toggle("text-stone-700", sandbox)
    }
    if (this.hasConfirmRowTarget) this.confirmRowTarget.hidden = sandbox
  }

  async run(event) {
    event.preventDefault()

    const key = this.key()
    if (key === "") return this.write("", this.needsKeyValue)

    const writing = this.verbValue !== "GET"
    if (writing && !this.sandbox() && !this.confirmTarget.checked) {
      return this.write("", this.needsConfirmValue)
    }

    const base = this.sandbox() ? this.sandboxBaseValue : this.liveBaseValue
    this.write("…", "")

    try {
      const response = await fetch(base + this.filledPath(), {
        method: this.verbValue,
        headers: this.headers(key, writing),
        body: writing && this.hasBodyTarget ? this.bodyTarget.value : undefined
      })
      const text = await response.text()
      this.write(`${response.status} ${response.statusText}`, this.pretty(text))
    } catch (error) {
      this.write("", `${error}`)
    }
  }

  headers(key, writing) {
    const headers = { Authorization: `Bearer ${key}`, Accept: "application/json" }
    if (writing) {
      headers["Content-Type"] = "application/json"
      headers["Idempotency-Key"] = crypto.randomUUID()
    }
    return headers
  }

  // {id} in het pad wordt wat de gebruiker in het vakje zette.
  filledPath() {
    return this.pathTargets.reduce(
      (path, field) => path.replace(`{${field.dataset.parameter}}`, encodeURIComponent(field.value.trim())),
      this.templateValue
    )
  }

  pretty(text) {
    try {
      return JSON.stringify(JSON.parse(text), null, 2)
    } catch (_error) {
      return text
    }
  }

  write(status, body) {
    if (this.hasStatusTarget) this.statusTarget.textContent = status
    if (this.hasOutputTarget) {
      this.outputTarget.textContent = body
      this.outputTarget.hidden = body === ""
    }
  }
}
